// The Unsaid — transcript + reveal handoff store (isolated to FioNova Web).
// Transcript & generated reveal live in sessionStorage (scoped to the current
// journey, prevents cross-session mixing). The reveal outcome + feedback are
// kept in localStorage as the structured handoff for later (approved) phases.
// NOT connected to any production persistence.

const T_KEY = "fionova_unsaid_transcript";
const R_KEY = "fionova_unsaid_reveal_cache";
const O_KEY = "fionova_unsaid_outcome";
const XR_KEY = "fionova_xray_cache";
const XO_KEY = "fionova_xray_outcome";
const TP_KEY = "fionova_todayplan_cache";
const TPO_KEY = "fionova_todayplan_outcome";
const FS_KEY = "fionova_futureself_cache";
const FSO_KEY = "fionova_futureself_outcome";
const FSC_KEY = "fionova_futureself_chat";
const PC_KEY = "fionova_purpose_chat";
const PO_KEY = "fionova_purpose_outcome";
const HIST_KEY = "fionova_journey_history";
const BK_KEY = "fionova_book_outcome";

const sGet = (k) => { try { const v = sessionStorage.getItem(k); return v ? JSON.parse(v) : null; } catch { return null; } };
const sSet = (k, v) => { try { sessionStorage.setItem(k, JSON.stringify(v)); } catch {} };

export function transcriptHash(messages = []) {
  const first = messages.find((m) => m.role === "user")?.text || "";
  const last = messages[messages.length - 1]?.text || "";
  return `${messages.length}|${first.slice(0, 24)}|${last.slice(0, 24)}`;
}

export const unsaidStore = {
  setTranscript: (messages) => sSet(T_KEY, messages),
  getTranscript: () => sGet(T_KEY) || [],

  // Cache the generated reveal against its transcript hash so refresh/navigation
  // reuse the SAME reveal for the SAME conversation (no remixing, no re-call).
  setRevealCache: (hash, reveal) => sSet(R_KEY, { hash, reveal }),
  getRevealCache: (hash) => {
    const c = sGet(R_KEY);
    return c && c.hash === hash ? c.reveal : null;
  },

  saveOutcome: (outcome) => {
    try { localStorage.setItem(O_KEY, JSON.stringify({ ...outcome, savedAt: new Date().toISOString() })); } catch {}
  },
  getOutcome: () => { try { const v = localStorage.getItem(O_KEY); return v ? JSON.parse(v) : null; } catch { return null; } },

  // Emotional X-Ray (isolated to FioNova Web dev architecture).
  setXrayCache: (hash, xray) => sSet(XR_KEY, { hash, xray }),
  getXrayCache: (hash) => { const c = sGet(XR_KEY); return c && c.hash === hash ? c.xray : null; },
  saveXrayOutcome: (outcome) => {
    try { localStorage.setItem(XO_KEY, JSON.stringify({ ...outcome, savedAt: new Date().toISOString() })); } catch {}
  },
  getXrayOutcome: () => { try { const v = localStorage.getItem(XO_KEY); return v ? JSON.parse(v) : null; } catch { return null; } },

  // Today Plan (isolated to FioNova Web dev architecture).
  setPlanCache: (hash, plan) => sSet(TP_KEY, { hash, plan }),
  getPlanCache: (hash) => { const c = sGet(TP_KEY); return c && c.hash === hash ? c.plan : null; },
  savePlanOutcome: (outcome) => {
    try { localStorage.setItem(TPO_KEY, JSON.stringify({ ...outcome, savedAt: new Date().toISOString() })); } catch {}
  },
  getPlanOutcome: () => { try { const v = localStorage.getItem(TPO_KEY); return v ? JSON.parse(v) : null; } catch { return null; } },

  // Future Self (isolated to FioNova Web dev architecture).
  setFutureCache: (hash, future) => sSet(FS_KEY, { hash, future }),
  getFutureCache: (hash) => { const c = sGet(FS_KEY); return c && c.hash === hash ? c.future : null; },
  saveFutureOutcome: (outcome) => {
    try { localStorage.setItem(FSO_KEY, JSON.stringify({ ...outcome, savedAt: new Date().toISOString() })); } catch {}
  },
  getFutureOutcome: () => { try { const v = localStorage.getItem(FSO_KEY); return v ? JSON.parse(v) : null; } catch { return null; } },

  // Future Self conversation transcript — survives refresh within the same
  // journey (keyed by transcript hash so a new journey starts fresh).
  setFutureChat: (hash, messages, phase) => sSet(FSC_KEY, { hash, messages, phase }),
  getFutureChat: (hash) => { const c = sGet(FSC_KEY); return c && c.hash === hash ? c : null; },

  // Purpose Engine (isolated to FioNova Web dev architecture). Conversation
  // persists across refresh (session, hash-keyed); reflection + extracted memory
  // kept in localStorage as the evolving Companion Memory handoff for later phases.
  setPurposeChat: (hash, messages, phase) => sSet(PC_KEY, { hash, messages, phase }),
  getPurposeChat: (hash) => { const c = sGet(PC_KEY); return c && c.hash === hash ? c : null; },
  savePurposeOutcome: (outcome) => {
    try { localStorage.setItem(PO_KEY, JSON.stringify({ ...outcome, savedAt: new Date().toISOString() })); } catch {}
  },
  getPurposeOutcome: () => { try { const v = localStorage.getItem(PO_KEY); return v ? JSON.parse(v) : null; } catch { return null; } },

  // Private history log (silently persisted; browse/reopen UI arrives with the
  // full post-subscription app). Purpose is allowed to evolve — we append, never lock.
  appendHistory: (entry) => {
    try {
      const list = JSON.parse(localStorage.getItem(HIST_KEY) || "[]");
      list.push({ ...entry, at: new Date().toISOString() });
      localStorage.setItem(HIST_KEY, JSON.stringify(list.slice(-50)));
    } catch {}
  },
  getHistory: () => { try { return JSON.parse(localStorage.getItem(HIST_KEY) || "[]"); } catch { return []; } },

  // Personalized book recommendation (may be { book: null } when no strong match).
  saveBookOutcome: (outcome) => { try { localStorage.setItem(BK_KEY, JSON.stringify({ ...outcome, savedAt: new Date().toISOString() })); } catch {} },
  getBookOutcome: () => { try { const v = localStorage.getItem(BK_KEY); return v ? JSON.parse(v) : null; } catch { return null; } },
};
